import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { environment } from "../environments/environment";
import { Person } from "../models/person";
import { RootState } from "./store";

// API Calls Start
const apiGetProfileData = async (): Promise<Person> => {
    const baseURL = environment.apiURL;
    try {
        const email = localStorage.getItem('userEmail');

        if (!email) {
            throw new Error('No email found in localStorage');
        }

        const response = await axios.get(`${baseURL}/people`, {
            params: {
                fields: 'all,photoAttachment,photoAttachment.downloadURL',
                email: email
            }
        });

        return response.data[0];
    } catch (error) {
        console.error('getProfileData - Error:', error);
        throw error;
    }
}

const apiGetProfilePicture = async (): Promise<string> => {
    const baseURL = environment.apiURL;
    const response = await axios.get(`${baseURL}/profile?fields=all`);
    return response.data.profilePicture;
}

const apiUpdateProfilePicture = async (profilePicture: string, isRemoveRequest: boolean): Promise<Person> => {
    const baseURL = environment.apiURL;
    const response = await axios.put(`${baseURL}/profile/profilepicture`, { photoAttachment: profilePicture, isRemoveRequest: isRemoveRequest });
    return response.data;
}

const apiGetProfiles = async (): Promise<Person[]> => {
    const baseURL = environment.apiURL;
    const email = localStorage.getItem('userEmail')
    const response = await axios.get(`${baseURL}/people/byEmail/${email}?fields=all,locations.all,oemSubscriptions,oemSubscriptions.all`);
    return response.data;
}

const apiGetFQMProfiles = async (): Promise<Person[]> => {
    const baseURL = environment.apiURL;
    const email = localStorage.getItem('userEmail')
    const response = await axios.get(`${baseURL}/people/fqmEmails/${email}?fields=all,locations.all,oemSubscriptions,oemSubscriptions.all`);
    return response.data;
}

const apiUpdateProfiles = async (person: Person): Promise<Person> => {
    const baseURL = environment.apiURL;
    const response = await axios.put(`${baseURL}/people/${person.id}?fields=all`, person);
    return response.data;
}

const apiUpdateFQMProfiles = async (person: Person): Promise<Person> => {
    const baseURL = environment.apiURL;
    const response = await axios.put(`${baseURL}/people/fqrEmails/${person.id}?fields=all`, person);
    return response.data;
}

const apiUpdatePassword = async (newPassword: string, oldPassword:string): Promise<Person> => {
    const baseURL = environment.apiURL;
    const response = await axios.post(`${baseURL}/profile/password`, { password: newPassword, oldPassword });
    return response.data;
}

const apiSendPasswordResetEmail = async (email: string): Promise<void> => {
    const baseURL = environment.apiURL;
    const response = await axios.post(`${baseURL}/login/recoveryRequest`, { email });
    return response.data;
}
// API Calls End

// Thunks Start
export const getProfileData = createAsyncThunk(
    'profile/get',
    async () => {
        const profileData = await apiGetProfileData();
        return profileData;
    }
);

export const getProfilePicture = createAsyncThunk(
    'profile/getPicture',
    async () => {
        const profilePicture = await apiGetProfilePicture();
        return profilePicture;
    }
);

export const updateProfilePicture = createAsyncThunk(
    'profile/updatePicture',
    async (data: { profilePicture: string, isRemoveRequest: boolean }) => {
        const profileData = await apiUpdateProfilePicture(data.profilePicture, data.isRemoveRequest);
        return profileData;
    }
);

export const updateProfiles = createAsyncThunk(
    'profile/update',
    async (person: Person) => {
        const profileData = await apiUpdateProfiles(person);
        return profileData;
    }
);

export const updateFQMProfiles = createAsyncThunk(
    'profile/updateFQM',
    async (person: Person) => {
        const profileData = await apiUpdateFQMProfiles(person);
        return profileData;
    }
);

export const updatePassword = createAsyncThunk(
    'profile/updatePassword',
    async (data: { newPassword: string, oldPassword: string }) => {
        const profileData = await apiUpdatePassword(data.newPassword, data.oldPassword);
        return profileData;
    }
);

export const sendPasswordResetEmail = createAsyncThunk(
    'profile/sendPasswordResetEmail',
    async (email: string) => {
        const profileData = await apiSendPasswordResetEmail(email);
        return profileData;
    }
);

export const getProfiles = createAsyncThunk(
    'profile/getProfiles',
    async () => {
        const profileData = await apiGetProfiles();
        return profileData;
    }
);

export const getFQMProfiles = createAsyncThunk(
    'profile/getFQMProfiles',
    async () => {
        const profileData = await apiGetFQMProfiles();
        return profileData;
    }
);
// Thunks End


// Slice Start
interface ProfileState {
  profiles: Person[];
  fqmProfiles: Person[];
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: ProfileState = {
  profiles: [],
  fqmProfiles: [],
  status: 'idle',
  error: null
};

const profileSlice = createSlice({
  name: "profile",
  initialState,
  reducers: {
    clearProfiles: (state) => {
      state.profiles = [];
      state.fqmProfiles = [];
      state.status = 'idle';
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getProfiles.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getProfiles.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.profiles = action.payload;
        state.error = null;
      })
      .addCase(getProfiles.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to fetch profiles';
      })
      .addCase(getFQMProfiles.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getFQMProfiles.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.fqmProfiles = action.payload;
        state.error = null;
      })
      .addCase(getFQMProfiles.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to fetch FQM profiles';
      });
  },
});

// Export actions and reducer
export const { clearProfiles } = profileSlice.actions;

// Selectors
export const selectAllProfiles = (state: RootState) => state.profile.profiles;
export const selectFQMProfiles = (state: RootState) => state.profile.fqmProfiles;
export const selectCombinedProfiles = (state: RootState) => [...state.profile.profiles, ...state.profile.fqmProfiles];
export const selectProfilesStatus = (state: RootState) => state.profile.status;
export const selectProfilesError = (state: RootState) => state.profile.error;

export default profileSlice.reducer;